<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import Flatpickr from 'svelte-flatpickr'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'
    import { DateTime } from 'luxon'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { makeSortString } from 'hydria/utils/strings'
    import { toDate, toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import CodeMirror from 'admin/components/CodeMirror.svelte'
    import DateEl from 'hydria/components/Date.svelte'
    import Dropdown from 'hydria/components/Dropdown.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const LIMIT = 200
    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        done: '3state',
        due_lower: 'string',
        due_upper: 'string',
        author_id: 'string',
    }
    const mappingOrder = ['q']

    // Search options for the backend
    interface SOptions {
        q: string
        done: null | boolean
        due_lower: string
        due_upper: string
        author_id: string
    }
    const defaults: SOptions = {
        q: '',
        done: null,
        due_lower: '',
        due_upper: '',
        author_id: '',
    }

    interface IEvent {
        id: string
        author_id?: string
        author_name?: string
        ctime?: DateTime
        mtime?: DateTime
        // due is a "date", not a datetime. Need to be careful with timezones here.
        due: DateTime | null
        done: boolean
        text: string
        revision?: number
    }

    // Search result from the server
    interface ISearchResult {
        items: IEvent[]
        total_items: number
        authors: { [key: string]: string }
    }

    interface IDay {
        ymd: number
        date: DateTime
        past: boolean
        today: boolean
        future: boolean
        hasNotDone: boolean
        items: IEvent[]
    }
    interface IMonth {
        ym: number
        date: DateTime
        days: IDay[]
    }
    interface IAuthor {
        id: string
        name: string
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()

        if (qs != $querystring) {
            let path = qs == '' ? '/events' : `/events?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/events' : `/events?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    let results = writable([] as IEvent[])
    let yearMonths: IMonth[] = $state([])
    let authors: IAuthor[] = $state([])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    function doSearch() {
        updating = true
        if (editItem) editItem = null

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/event/1/', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = 0

    function loadSearchResult(data: ISearchResult) {
        let litems: IEvent[] = []
        // const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            for (const k of ['ctime', 'mtime']) {
                if (item[k]) item[k] = toLuxon(item[k])
            }
            if (item['due']) item['due'] = toLuxon(item['due'], { assumeLocal: true })

            litems.push(item)
        }

        results.set(litems)

        // Now, we group into days..

        yearMonths.length = 0
        const today = parseInt(DateTime.now().toFormat('yyyyMMdd'), 10)
        let cYm: IMonth | null = null
        let cDay: IDay | null = null

        for (const item of litems) {
            if (!item.due) continue
            const ymd = parseInt(item.due.toFormat('yyyyMMdd'), 10)
            const ym = parseInt(item.due.toFormat('yyyyMM'), 10)

            if (!cYm || cYm.ym !== ym) {
                cYm = {
                    ym: ym,
                    date: item.due,
                    days: [],
                }
                cDay = null
                yearMonths.push(cYm)
            }

            if (!cDay || cDay.ymd !== ymd) {
                cDay = {
                    ymd: ymd,
                    date: item.due,
                    items: [],
                    past: ymd < today,
                    today: ymd === today,
                    future: ymd > today,
                    hasNotDone: false,
                }
                cYm.days.push(cDay)
            }

            if (!item.done) cDay.hasNotDone = true

            cDay.items.push(item)
        }

        yearMonths = yearMonths

        authors = orderBy(
            Object.entries(data.authors).map(([i, n]) => {
                return { id: i, name: n }
            }),
            [(i) => makeSortString(i.name)],
            ['asc'],
        )

        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    let dueLowerText: Date | undefined = $state()
    let dueUpperText: Date | undefined = $state()
    function loadIntoForm() {
        $text = sOptions.q
        dueLowerText = sOptions.due_lower ? toDate(sOptions.due_lower, true) : undefined
        dueUpperText = sOptions.due_upper ? toDate(sOptions.due_upper, true) : undefined
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    const flatOpts_lower = {
        element: '#due_lower',
    }
    const flatOpts_upper = {
        element: '#due_upper',
    }

    let saving = $state(false)

    let editor: any

    let togglingDoneIds: Set<string> = $state(new Set())
    let editItem: IEvent | null = $state(null)
    let editText: string = $state()
    // let editDue: DateTime
    let editDueText: string = $state()
    let editDueDate: string = $state() // Will swap to a "Date()" when selecting but we don't give a shit
    let editShowDelete: boolean = $state()

    function toggleEdit(item: IEvent | true | null = null) {
        if (item == null || editItem === item || (item === true && editItem && editItem.id == '')) {
            editItem = null
            return
        }

        // Close the current editor first, even if it probably won't happen due to the logic in the HTML
        editItem = null

        if (item === true) {
            item = {
                id: '',
                author_id: '',
                author_name: '',
                due: null,
                done: false,
                text: '',
            }
        }

        editText = item.text
        // editDue = item.due
        editDueText = item.due ? item.due.toFormat('yyyy-MM-dd') : ''
        editDueDate = item.due ? editDueText : ''
        editShowDelete = false
        editItem = item
    }

    async function deleteEditItem() {
        if (editItem == null) return
        saving = true
        editShowDelete = false
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('DELETE', `/json/event/1/${editItem.id}`)
        } catch (e) {
            console.error('Failed to delete:', e)
            notify({
                type: 'danger',
                title: 'Événement',
                text: 'Erreur d’effacement de l’événement.',
            })
            return
        } finally {
            saving = false
        }

        editItem = null
        doSearch()
    }
    async function applyEdit() {
        if (editItem == null) return
        saving = true
        editShowDelete = false
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('POST', `/json/event/1/${editItem.id}`, { text: editText, due: editDueText })
        } catch (e) {
            console.error('Failed to save edit:', e)
            notify({
                type: 'danger',
                title: 'Événement',
                text: 'Erreur de sauvegarde de l’édition.',
            })
            return
        } finally {
            saving = false
        }

        editItem = null
        doSearch()
    }

    function saveNewOk(editText: string, editDueText: string) {
        if (!editText || editText.trim().length < 1) return false
        if (!editDueText) return false
        return true
    }
    async function saveNew() {
        saving = true
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('POST', '/json/event/1/', { done: false, text: editText, due: editDueText })
        } catch (e) {
            console.error('Failed to create new event:', e)
            notify({
                type: 'danger',
                title: 'Événement',
                text: 'Erreur de sauvegarde du nouvel événement.',
            })
            return
        } finally {
            saving = false
        }

        editItem = null
        doSearch()
    }

    async function toggleDone(item: IEvent) {
        togglingDoneIds = togglingDoneIds.add(item.id)

        let r: SimpleFetchResult
        try {
            r = await simpleFetch(item.done ? 'DELETE' : 'PUT', `/json/event/1/${item.id}/done`)
        } catch (e) {
            console.error('Failed to toggle DONE on item:', e)
            notify({
                type: 'danger',
                title: 'Événement',
                text: 'Le statut « fait » n’a pas pu être changé suite à une erreur.',
            })
            return
        } finally {
            togglingDoneIds.delete(item.id)
            togglingDoneIds = togglingDoneIds
        }

        doSearch()
    }

    $effect(() => {
        if (editor) {
            if (!editItem) editor = null
            else editor.setOption('readOnly', saving)
        }
    })
</script>

<style>
    .form-event-list {
        display: grid;
        gap: 0.25em;
        grid-template-columns: minmax(10em, 4fr) minmax(140px, 4fr) minmax(140px, 4fr) 1fr 10em auto;
        grid-template-areas: 'text dmin dmax . author done';
    }
    .fel-text {
        grid-area: text;
    }

    .fel-due-min {
        grid-area: dmin;
    }
    .fel-due-max {
        grid-area: dmax;
    }

    .fel-author {
        grid-area: author;
    }
    .fel-done {
        grid-area: done;
    }

    .event-day {
        position: relative;
        display: grid;
        grid-template-columns: 50px 1fr;
        min-height: 50px;
    }
    .event-day:not(:last-child) {
        border-bottom: 1px solid #ddd;
        padding-bottom: 2em;
        margin-bottom: 1.3em;
    }
    .event-day.not-done.past .day-poster h2 {
        color: #b00;
    }
    .event-day.not-done.today .day-poster h2 {
        color: #449;
    }
    .day-poster h2 {
        margin: 0;
        line-height: 50px;
        height: 50px;
        width: 40px;
        text-align: right;
    }
    .day-item {
        grid-column: 2 / span 1;
        display: grid;
        grid-gap: 0 10px;
        grid-template-columns: 1fr auto;
        /* margin-left: 50px; */
        position: relative;
    }
    .day-item:not(:last-child) {
        border-bottom: 1px solid #ddd;
        padding-bottom: 1.5em;
    }
    .day-item + .day-item > .text > .text-display {
        padding-top: 10px;
    }
    .day-item .controls {
        grid-column: 2 / span 1;
        /* position: absolute; */
        /* top: 0; */
        /* right: 0; */
        /* width: 100px; */
        /* padding: 10px 0 0; */
        text-align: right;
        justify-items: end;

        padding-top: 4px;

        display: grid;
        height: 100%;
        grid-template-rows: auto 1fr auto;
        max-width: 160px;
    }
    .event-author {
        align-self: end;
        font-size: 0.9em;
    }
    .event-ctime {
        font-size: 0.8em;
    }
    .day-item .text-display {
        white-space: pre-line;
        padding-top: 20px;
    }
    .day-item .text-edit {
        padding-top: 13px;
    }
    .day-item .text-edit-controls {
        text-align: right;
        margin-top: 5px;
    }
</style>

<Breadcrumb crumbs={[{ txt: tgettext('Événements') }]} />

<div class="d-grid gap-4">
    <div class="d-flex gap-4">
        <div class="card" style="flex: 1 0 0;">
            <div class="card-title text-primary">{$_('Search parameters')}</div>
            <div class="card-body">
                <form
                    onsubmit={(e) => {
                        e.preventDefault()
                        submit()
                    }}
                >
                    <div class="form-event-list">
                        <div class="fel-text">
                            <div class="input-group">
                                <input type="text" class="form-control" id="domain-text" bind:this={mainInput} bind:value={$text} />
                                <button class="btn btn-primary search" type="submit"><Icon inline icon="fa:search" /></button>
                            </div>
                        </div>

                        <div class="fel-due-min">
                            <Flatpickr options={flatOpts_lower} bind:formattedValue={sOptions.due_lower} bind:value={dueLowerText} dateFormat="Y-m-d" element="#due_lower">
                                <div id="due_lower" class="input-group">
                                    <input type="text" class="form-control" data-input placeholder="Tombée min" />
                                    <button class="btn btn-outline-secondary" type="button" data-clear disabled={!sOptions.due_lower}><Icon inline icon="fa:times" /></button>
                                </div>
                            </Flatpickr>
                        </div>
                        <div class="fel-due-max">
                            <Flatpickr options={flatOpts_upper} bind:formattedValue={sOptions.due_upper} bind:value={dueUpperText} dateFormat="Y-m-d" element="#due_upper">
                                <div id="due_upper" class="input-group">
                                    <input type="text" class="form-control" data-input placeholder="Tombée max" />
                                    <button class="btn btn-outline-secondary" type="button" data-clear disabled={!sOptions.due_upper}><Icon inline icon="fa:times" /></button>
                                </div>
                            </Flatpickr>
                        </div>

                        <div class="fel-author">
                            <Dropdown
                                items={authors}
                                placeholder="Auteur"
                                onSelect={(e) => {
                                    sOptions.author_id = e
                                    setQueryString(sOptions)
                                }}
                                text={'name'}
                                id={'id'}
                                selected={sOptions.author_id}
                                empty={''}
                                clear={''}
                                disabled={updating}
                                alignRight={true}
                                block={true}
                            />
                        </div>

                        <div class="fel-done">
                            <div class="btn-group">
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    disabled={updating}
                                    class:active={sOptions.done === true}
                                    onclick={() => {
                                        sOptions.done = sOptions.done === true ? null : true
                                        setQueryString(sOptions)
                                    }}><Icon inline icon="fa:check-square-o" /></button
                                >
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    disabled={updating}
                                    class:active={sOptions.done === false}
                                    onclick={() => {
                                        sOptions.done = sOptions.done === false ? null : false
                                        setQueryString(sOptions)
                                    }}><Icon inline icon="fa:square-o" /></button
                                >
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <div class="card" style="flex: 0 0 auto;">
            <div class="card-title text-primary">Actions</div>
            <div class="card-body">
                <button type="button" class="btn btn-primary" disabled={saving || (editItem != null && editItem.id != '') || togglingDoneIds.size != 0} onclick={() => toggleEdit(true)}>{$_('Nouvel événement')}</button>
            </div>
        </div>
    </div>

    {#if editItem && editItem.id == ''}
        <div class="card event-new">
            <div class="card-title text-primary">Nouvel événement</div>
            <div class="card-body">
                <CodeMirror
                    options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: false, mode: 'plain', showTrailingSpace: true }}
                    bind:value={editText}
                    setEditor={(e) => {
                        editor = e
                    }}
                />
            </div>
            <div class="card-footer text-end">
                <div class="float-left">
                    <Flatpickr options={{ element: '#edit-due' }} bind:formattedValue={editDueText} bind:value={editDueDate} dateFormat="Y-m-d" element="#edit-due">
                        <div id="edit-due"><input type="text" class="form-control" data-input placeholder="Tombée" disabled={saving} /></div>
                    </Flatpickr>
                </div>

                {#if saving}
                    <button type="button" class="btn btn-info" disabled
                        ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        Sauvegarde…</button
                    >
                {:else}
                    <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => toggleEdit()}>Annuler</button>
                    <button type="button" class="btn btn-sm btn-primary" disabled={!saveNewOk(editText, editDueText)} onclick={() => saveNew()}>Sauvegarder</button>
                {/if}
            </div>
        </div>
    {/if}

    <div class="events-page d-grid gap-4">
        {#if loading}
            <div class="card">
                <div class="card-title text-primary"><DateEl value="now" format="MMMM yyyy" transform="ucfirst" /></div>
                <div class="card-body">
                    <LoadingPattern pattern={['x', 'x', 'x']} />
                </div>
            </div>
        {:else}
            {#each yearMonths as yearMonth}
                <div class="card">
                    <div class="card-title text-primary d-flex">
                        <div style="flex: 1 0 0">
                            <DateEl value={yearMonth.date} format="MMMM yyyy" transform="ucfirst" />
                        </div>
                        {#if updating}
                            <div style="flex: 0 0 auto"><div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div></div>
                        {/if}
                    </div>
                    <div class="card-body">
                        {#each yearMonth.days as day}
                            <div class="event-day" class:today={day.today} class:future={day.future} class:past={day.past} class:not-done={day.hasNotDone}>
                                <div class="day-poster"><h2>{day.date.toFormat('d')}</h2></div>

                                {#each day.items as item}
                                    <div class="day-item">
                                        <div class="text">
                                            {#if editItem === item}
                                                <div class="text-edit">
                                                    <CodeMirror options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: false, mode: 'plain', showTrailingSpace: true }} bind:value={editText} bind:editor />

                                                    <div class="text-edit-controls">
                                                        <div class="pull-left">
                                                            <Flatpickr options={{ element: '#edit-due' }} bind:formattedValue={editDueText} bind:value={editDueDate} dateFormat="Y-m-d" element="#edit-due">
                                                                <div id="edit-due"><input type="text" class="form-control" data-input placeholder="Tombée" disabled={saving} /></div>
                                                            </Flatpickr>
                                                        </div>
                                                        {#if editShowDelete}
                                                            <div class="btn-group btn-group-sm">
                                                                <button type="button" class="btn btn-danger" onclick={() => deleteEditItem()}>Confirmer</button>
                                                                <button type="button" class="btn btn-warning" onclick={() => (editShowDelete = false)}>Ne pas effacer</button>
                                                            </div>
                                                        {:else}
                                                            <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => (editShowDelete = true)}>Effacer</button>
                                                        {/if}

                                                        {#if saving}
                                                            <button type="button" class="btn btn-info" disabled
                                                                ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                                                Sauvegarde…</button
                                                            >
                                                        {:else}
                                                            <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => toggleEdit()}>Annuler</button>
                                                            <button type="button" class="btn btn-sm btn-primary" onclick={() => applyEdit()}>Sauvegarder</button>
                                                        {/if}
                                                    </div>
                                                </div>
                                            {:else}
                                                <div class="text-display" class:text-muted={item.done}>{item.text}</div>
                                            {/if}
                                        </div>
                                        <div class="controls">
                                            <div class="event-actions btn-group btn-group-sm">
                                                <button type="button" class="btn btn-outline-secondary" class:active={editItem === item} onclick={() => toggleEdit(item)} disabled={saving || (editItem != null && editItem !== item) || togglingDoneIds.size != 0}><Icon inline icon="fa:pencil-square-o" /></button>
                                                {#if togglingDoneIds.has(item.id)}
                                                    <button type="button" class="btn btn-outline-secondary" disabled><div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div></button>
                                                {:else}
                                                    <button type="button" class="btn btn-outline-secondary" onclick={() => toggleDone(item)} disabled={saving || editItem != null || togglingDoneIds.size != 0}><Icon inline icon={item.done ? 'fa:check-square' : 'fa:square-o'} /></button>
                                                {/if}
                                            </div>

                                            <div class="event-author">
                                                {#if item.author_id}
                                                    <a href={spath(`/user/${item.author_id}`)} use:sanchor>{item.author_name ? item.author_name : '?'}</a>
                                                {/if}
                                            </div>

                                            <div class="event-ctime">
                                                <DateEl value={item.ctime} format="h-table" />
                                            </div>
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        {/each}
                    </div>
                </div>
            {/each}
        {/if}
    </div>
</div>
